import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mail, RefreshCcw, AlertCircle, Search, Download } from '../../ui/icons';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Colors, { withAlpha } from '../../../constants/Colors';
import { exportToCsv } from '../../../utils/csv';
import {
  opsContactCounterApi,
  ContactCounterRow,
  ContactCounterTotals,
} from '../../../services/api';

const PAGE_SIZE = 50;

const METRICS: { key: keyof ContactCounterRow; label: string; color: string }[] = [
  { key: 'contact_visit', label: 'Visits', color: Colors.primary },
  { key: 'contact_submitted', label: 'Submitted', color: '#2F9E44' },
];

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'Recently updated' },
  { value: 'contact_visit', label: 'Visits' },
  { value: 'contact_submitted', label: 'Submitted' },
  { value: 'email', label: 'Email' },
];

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-GB');
}

function MetricChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    // Same frosted-glass tile as Purchasers' "by school" strip (.purchaser-school-tile).
    <div className="purchaser-school-tile" style={{ flex: '1 1 120px', minWidth: '120px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: Colors.textLight, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ fontSize: '19px', fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

// One small tile per metric (same idea as the totals row above): label + count in the
// metric's colour. Zero counts stay neutral so the tiles with activity stand out.
function CounterTiles({ row }: { row: ContactCounterRow }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${METRICS.length}, minmax(0, 1fr))`, gap: '6px' }}>
      {METRICS.map((m) => {
        const value = Number(row[m.key] || 0);
        const active = value > 0;
        return (
          <div
            key={m.key}
            title={`${m.label}: ${value.toLocaleString()}`}
            style={{
              minWidth: 0,
              padding: '6px 6px',
              borderRadius: '8px',
              textAlign: 'center',
              border: `1px solid ${active ? withAlpha(m.color, 0.28) : Colors.divider}`,
              background: active ? withAlpha(m.color, 0.08) : 'rgba(255, 255, 255, 0.5)',
            }}
          >
            <div style={{ fontSize: '9px', fontWeight: 700, color: Colors.textLight, letterSpacing: '0.02em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {m.label}
            </div>
            <div style={{ marginTop: '2px', fontSize: '16px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: active ? m.color : Colors.inactive }}>
              {value.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ContactCounterPanel() {
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [sort, setSort] = useState('updated_at');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ContactCounterRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totals, setTotals] = useState<ContactCounterTotals | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await opsContactCounterApi.list({
        q: appliedSearch || undefined,
        sort,
        offset,
        limit: PAGE_SIZE,
      });

      if (response.success && response.data?.success) {
        const page = response.data.rows || [];
        setRows((prev) => (append ? [...prev, ...page] : page));
        setTotal(response.data.total ?? page.length);
        setHasMore(Boolean(response.data.has_more));
        setTotals(response.data.totals || null);
        setError(null);
      } else {
        if (!append) {
          setRows([]);
          setTotals(null);
        }
        setHasMore(false);
        setError(response.error || response.data?.error || 'Failed to load contact counters');
      }

      if (append) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    [appliedSearch, sort],
  );

  useEffect(() => {
    fetchPage(0, false);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [fetchPage]);

  const onListScroll = () => {
    const el = listRef.current;
    if (!el || !hasMore || loadingMoreRef.current || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      fetchPage(rows.length, true);
    }
  };

  const handleExportCsv = () => {
    exportToCsv(
      `contact-counter-${new Date().toISOString().slice(0, 10)}.csv`,
      rows,
      [
        { header: 'Email', value: (row) => row.email },
        { header: 'User ID', value: (row) => row.user_id },
        { header: 'Contact Visits', value: (row) => row.contact_visit },
        { header: 'Contact Submitted', value: (row) => row.contact_submitted },
        { header: 'Updated At', value: (row) => row.updated_at },
      ]
    );
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Mail size={24} />
        Contact Counter
      </h2>

      <Card
        title="Contact Us activity by user"
        description="contact_visit / contact_submitted counts from purchase-paywall-counter"
        headerAction={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="outline" onClick={() => fetchPage(0, false)} loading={loading}>
              <RefreshCcw size={14} />
              Refresh
            </Button>
            <Button variant="outline" onClick={handleExportCsv} disabled={rows.length === 0}>
              <Download size={14} />
              Export {rows.length < total ? 'Loaded' : 'All'}
            </Button>
          </div>
        }
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '16px',
          }}
        >
          <MetricChip label="Users" value={totals?.users || 0} color={Colors.text} />
          <MetricChip label="Visits" value={totals?.contact_visit || 0} color={Colors.primary} />
          <MetricChip label="Submitted" value={totals?.contact_submitted || 0} color="#2F9E44" />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            alignItems: 'flex-end',
            marginBottom: '14px',
          }}
        >
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: Colors.textLight, marginBottom: '6px' }}>
              SORT BY
            </label>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              style={{
                padding: '7px 10px',
                borderRadius: '8px',
                border: `1px solid ${Colors.divider}`,
                background: Colors.cardBackground,
                color: Colors.text,
                fontSize: '13px',
              }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 240px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: Colors.textLight, marginBottom: '6px' }}>
              SEARCH
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setAppliedSearch(search.trim());
                }}
                placeholder="email or user_id"
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: '8px',
                  border: `1px solid ${Colors.divider}`,
                  background: Colors.cardBackground,
                  color: Colors.text,
                  fontSize: '13px',
                }}
              />
              <Button variant="outline" onClick={() => setAppliedSearch(search.trim())}>
                <Search size={14} />
              </Button>
            </div>
          </div>
        </div>

        <div style={{ fontSize: '12px', color: Colors.textLight, marginBottom: '12px' }}>
          Showing {rows.length} of {total}
        </div>

        {error && (
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              color: Colors.error,
              marginBottom: '12px',
              fontSize: '13px',
            }}
          >
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <div
          ref={listRef}
          onScroll={onListScroll}
          style={{
            maxHeight: 'calc(100vh - 360px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '4px',
          }}
        >
          {loading && rows.length === 0 ? (
            <div style={{ color: Colors.textLight, fontSize: '13px', padding: '20px 0' }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ color: Colors.inactive, fontSize: '13px', padding: '20px 0' }}>
              No contact counter rows.
            </div>
          ) : (
            <>
              {/* Compact user cards, 2-3 per row on desktop (one column on narrow screens). */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))', gap: '10px', marginBottom: '10px' }}>
              {rows.map((row) => (
                <div
                  key={row.user_id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    minWidth: 0,
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: `1px solid ${Colors.divider}`,
                    background: Colors.cardBackground,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                      <div
                        title={row.email || ''}
                        style={{ minWidth: 0, fontWeight: 700, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {row.email || 'No email'}
                      </div>
                      <span style={{ flexShrink: 0, fontSize: '10px', color: Colors.textLight, whiteSpace: 'nowrap' }} title={`Updated ${formatWhen(row.updated_at)}`}>
                        {formatWhen(row.updated_at)}
                      </span>
                    </div>
                    <div
                      title={row.user_id}
                      style={{
                        marginTop: '2px',
                        fontSize: '10px',
                        color: Colors.textLight,
                        fontFamily: "'SF Mono', monospace",
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.user_id}
                    </div>
                  </div>
                  <CounterTiles row={row} />
                </div>
              ))}
              </div>
              {loadingMore && (
                <div style={{ color: Colors.textLight, fontSize: '12px', padding: '10px 0', textAlign: 'center' }}>
                  Loading more…
                </div>
              )}
              {!hasMore && rows.length > 0 && (
                <div style={{ color: Colors.inactive, fontSize: '12px', padding: '8px 0', textAlign: 'center' }}>
                  End of list
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
